import * as THREE from "three";
import { perf } from "../engine/perf";
import { ScenePose } from '../engine/scenePose';
import { RenderCellPass } from '../engine/renderCells';

/**
 * Depth partitioning — how this scene gets a usable depth buffer without paying
 * for a logarithmic one.
 *
 * THE PROBLEM. The camera spans 0.1 m (a hand in front of your face) to 10,000 km
 * (across a system). A 24-bit integer depth buffer cannot resolve a range ratio
 * of 1e8, so the client used `logarithmicDepthBuffer: true`. That works, and it
 * is expensive in a way nothing reports: three implements it by writing
 * `gl_FragDepth` in every fragment shader, and a shader that writes depth cannot
 * be early-Z rejected. In a forest, where a screen pixel is covered by many
 * leaves, the GPU shades every hidden one. Measured 2026-07-27 on an M4 Pro:
 * 49.2 ms per frame with it, 21.6 ms without — over half the frame.
 *
 * THE FIX. Draw the scene several times, each pass covering one slice of the
 * range, clearing DEPTH (not colour) between them and working from the farthest
 * slice inward. Each pass then gets the whole 24-bit buffer for a range ratio of
 * a few hundred, which is ample, and no shader writes depth.
 *
 * WHY IT COMPOSITES CORRECTLY. The slices are drawn far to near, so the nearer
 * slice owns the final colour. A tiny overlap before each boundary covers
 * Float32 projection/clipping roundoff; mathematically abutting clip planes can
 * otherwise reject the same thin strip of a hillside in BOTH passes.
 *
 * WHAT IT COSTS. One scene traversal and culling pass per slice. It is NOT three
 * times the drawing: each pass frustum-culls to its own slice, so a planet
 * surface draws its terrain in the near slice and almost nothing in the outer
 * two. Only straddling objects are submitted twice.
 *
 * THREE THINGS THAT WILL BITE IF CHANGED:
 *   - `scene.background`. If it is a Color, three sets `forceClear = true` and
 *     clears COLOUR at the top of every `render()` call — `autoClear = false` is
 *     not enough to stop it. Each slice then erases the one behind it and only
 *     the nearest survives, which on a planet looks perfectly fine (that slice
 *     is full of terrain) and in space is a black screen. The loop below hides
 *     the background from every pass but the outermost for exactly this reason.
 *   - The shadow map. Three renders shadows at the top of EVERY `render()` call,
 *     so a naive loop would rebuild a 4096² shadow map once per slice. The loop
 *     below takes manual control and rebuilds it exactly once.
 *   - The depth texture afterwards holds only the NEAREST slice — the others
 *     were cleared away. `nearSlice` is returned for that reason: anything
 *     reading depth downstream (fx/painterlyPost.ts) must linearise against
 *     that slice, not against the camera's full range.
 */

/**
 * Interior slice boundaries, in metres of view depth.
 *
 * Depth precision inside a slice [n, f] is roughly z²/(n·2²⁴) at distance z, so
 * the near plane of the FIRST slice sets the whole scheme's quality, and each
 * boundary buys precision for everything beyond it.
 *
 *   [0.1, 600]      → 0.2 m at 600 m. Everything you walk among.
 *   [600, 300k]     → 0.1 m at 30 km (the visible horizon), 9 m at the far end.
 *   [300k, camera.far] → 20 m at 10,000 km. These are planets; they are bigger.
 *
 * 600 m is also deliberately generous rather than precision-optimal, because it
 * doubles as the range of the depth signal the painterly post reads — see
 * `nearSlice`. Beyond it that signal saturates, which is continuous (no visible
 * ring) because saturating means "fully hazed", and 600 m in thick air already
 * is.
 */
const INTERIOR_BOUNDS = [600, 300_000] as const;

export interface DepthSlice {
  near: number;
  far: number;
}

/** GPU zone per slice index (0 = nearest). */
const SLICE_ZONE = ["near", "mid", "far"] as const;

export class DepthPartition {
  private readonly pose = new ScenePose();
  private readonly cells = new RenderCellPass();
  /** Reused so a per-frame render allocates nothing. */
  private scratch: DepthSlice[] = [];
  /** The zone the pass now drawing belongs to — read by the perf sentinel's
   *  onBeforeRender to close the shadow bracket and open the slice's. */
  sliceZone: (typeof SLICE_ZONE)[number] = "far";
  /** True only while the outermost slice's render() is running — the one that
   *  builds the shadow maps. The sentinel consumes it on its first draw. */
  shadowArmed = false;
  /**
   * The slice the current depth buffer describes. A STABLE object, updated in
   * place: callers pass it to the post chain as part of the same statement that
   * triggers the draw, so handing out a fresh object each frame would give them
   * last frame's numbers.
   */
  readonly nearSlice: DepthSlice = { near: 0.1, far: INTERIOR_BOUNDS[0] };
  /** Draw calls submitted across all slices last frame (diagnostics). */
  lastPasses = 0;

  /** Slice the camera's range at the interior bounds that fall inside it. */
  private compute(camera: THREE.PerspectiveCamera): DepthSlice[] {
    const near = Math.max(1e-4, camera.near);
    const far = Math.max(near * 2, camera.far);
    this.scratch.length = 0;
    let lo = near;
    for (const b of INTERIOR_BOUNDS) {
      if (b <= lo * 1.01 || b >= far) continue;
      this.scratch.push({ near: lo, far: b });
      // Four Float32 ulps of projected depth, converted back to view metres.
      // Keep the existing near-slice far/depth metadata exact. Only the next,
      // farther pass starts early; nearer geometry overwrites that narrow band.
      // The relative cap also bounds unusual camera near-plane settings.
      const overlap=Math.min((b-lo)*.01,4*1.1920928955078125e-7*b*b/lo);
      lo = b-overlap;
    }
    this.scratch.push({ near: lo, far });
    return this.scratch;
  }

  /**
   * Draw `scene` into the CURRENT render target, partitioned. Returns the
   * nearest slice — the one the surviving depth buffer describes.
   *
   * The camera's own near/far are restored before returning, so nothing outside
   * this call sees the temporary projection.
   */
  render(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    afterSlice?: (near:number,far:number)=>void,
  ): DepthSlice {
    const slices = this.compute(camera);
    const near0 = camera.near;
    const far0 = camera.far;
    const autoClear0 = renderer.autoClear;
    const shadowAuto0 = renderer.shadowMap.autoUpdate;
    const background0 = scene.background;

    // Build the shadow map ONCE. Left on automatic, three would rebuild it at the
    // top of every pass — the same 4096² render, two extra times, every frame.
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = true;

    this.pose.begin(scene);
    try {
      this.lastPasses = slices.length;
      for (let i = slices.length - 1; i >= 0; i--) {
        const s = slices[i];
        camera.near = s.near;
        camera.far = s.far;
        camera.updateProjectionMatrix();
        const outermost = i === slices.length - 1;
        // The outermost pass clears colour and depth and draws the background; the
        // rest must keep the colour drawn so far and clear only depth, or each
        // slice erases the one behind it. Hiding the background is half of that
        // instruction, not a tidy-up: a Color background forces a colour clear
        // whatever autoClear says (see the note above).
        renderer.autoClear = outermost;
        scene.background = outermost ? background0 : null;
        if (!outermost) renderer.clearDepth();
        // GPU attribution. The shadow maps are built inside the OUTERMOST render()
        // call, before its first draw, so that pass opens as "shadow" and the
        // perf sentinel (main.ts, the first opaque draw) flips it to the slice's
        // own zone. Inner slices never rebuild the map (needsUpdate is false).
        perf.gpuBegin(outermost ? "shadow" : SLICE_ZONE[i]);
        this.sliceZone = SLICE_ZONE[i];
        this.shadowArmed = outermost;
        try { this.cells.begin(scene, camera, outermost && renderer.shadowMap.enabled, i); renderer.render(scene, camera); }
        finally { this.cells.end(); }
        this.shadowArmed = false;
        afterSlice?.(s.near,s.far);
        perf.gpuEnd();
      }

    } finally {
      this.shadowArmed = false;
      this.pose.end(scene);
      renderer.autoClear = autoClear0;
      renderer.shadowMap.autoUpdate = shadowAuto0;
      scene.background = background0;
      camera.near = near0;
      camera.far = far0;
      camera.updateProjectionMatrix();
    }
    this.nearSlice.near = slices[0].near;
    this.nearSlice.far = slices[0].far;
    return this.nearSlice;
  }
}
